import { useState } from "react";

import { StickyNote } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useSetFieldNote } from "@/hooks/use-contest-submissions";
import type { ContestFieldNote } from "@/types/contest-submission";

import { formatDateTime } from "./format";

interface Props {
  submissionId: string;
  field: string;
  /** 저장된 메모 (없으면 null) */
  saved: ContestFieldNote | null;
}

/**
 * 응모 필드(시놉시스 등) 옆 심사 메모 — 필드별 즉시 저장 (심사 저장과 독립).
 * 로컬 상태는 마운트 시 seed — 호출부의 key={submissionId+field} 리마운트가
 * 제출물 전환 시 리셋을 담당하고, 배경 refetch 는 편집 입력을 덮지 않는다.
 */
export function FieldNoteEditor({ submissionId, field, saved }: Props) {
  const setFieldNote = useSetFieldNote();
  const savedNote = saved?.note ?? "";
  const [note, setNote] = useState(savedNote);

  const isDirty = note.trim() !== savedNote.trim();

  const handleSave = () => {
    setFieldNote.mutate({ submissionId, field, note: note.trim() });
  };

  return (
    <div className="space-y-1.5 rounded-md border border-dashed bg-amber-50/40 p-3">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
        <StickyNote className="h-3.5 w-3.5" />
        심사 메모
      </div>
      <Textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="이 항목에 대한 메모 (최대 2000자)"
        maxLength={2000}
        rows={3}
        className="bg-white text-sm"
      />
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] text-muted-foreground">
          {saved?.updatedAt
            ? `${saved.updatedBy ?? "-"} · ${formatDateTime(saved.updatedAt)}`
            : "저장된 메모 없음"}
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={handleSave}
          disabled={!isDirty || setFieldNote.isPending}
          className="h-7 px-2.5 text-xs"
        >
          {setFieldNote.isPending ? "저장 중…" : "저장"}
        </Button>
      </div>
    </div>
  );
}
